import { useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { buildSerpBulkStockAdjustmentContentHtml } from '@/components/Approval/@components/SerpBulkStockAdjustment';
import SerpBulkStockAdjustmentSnapshot from '@/components/Approval/@components/SerpBulkStockAdjustment/Snapshot';
import { getSerpBulkStockAdjustmentDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import type { SerpDocumentRef } from '@/utils/approval/serpDocument';
import {
  buildSerpBulkStockAdjustmentSubject,
  type SerpBulkStockAdjustmentDraftSource,
} from '@/utils/approval/serpBulkStockAdjustment';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SerpBulkStockAdjustmentFieldProps = {
  /** 신규 작성은 진입 쿼리, 편집은 저장 attributes(typeCode·data.sequence)의 SERP 문서 */
  serpDocument: SerpDocumentRef | null;
  isEditMode: boolean;
};

/**
 * SERP 재고실사 조정(다중)(210) — 레거시 appr_edit_forms_serp_inventory_multi.
 * 편집도 SERP 를 다시 조회해 표를 새로 그리고, 제목은 신규 작성 때만 자동으로 채움
 */
const SerpBulkStockAdjustmentField = ({
  serpDocument,
  isEditMode,
}: SerpBulkStockAdjustmentFieldProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'serpBulkStockAdjustment' });
  const subjectFilledRef = useRef(false);

  const typeCode = serpDocument?.typeCode;
  const sequence = serpDocument?.sequence;
  const { data, error, isLoading } = useSWR<SerpBulkStockAdjustmentDraftSource>(
    typeCode && sequence
      ? getSerpBulkStockAdjustmentDraftSourceUrl(typeCode, sequence)
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  useEffect(() => {
    const current = getValues('serpBulkStockAdjustment');
    if (
      current &&
      (current.typeCode !== typeCode || current.sequence !== sequence)
    ) {
      setValue('serpBulkStockAdjustment', null, { shouldDirty: false });
    }
  }, [typeCode, sequence, getValues, setValue]);

  useEffect(() => {
    if (!data || !typeCode || !sequence) return;
    if (data.attributes.typeCode !== typeCode) return;
    const source = data.display.source ?? {};
    const autoSubject = isEditMode
      ? ''
      : getValues('serpBulkStockAdjustment')?.autoSubject ??
        buildSerpBulkStockAdjustmentSubject(source, t);
    setValue(
      'serpBulkStockAdjustment',
      { typeCode, sequence, source, autoSubject },
      { shouldDirty: false },
    );
    if (isEditMode || subjectFilledRef.current) return;
    subjectFilledRef.current = true;
    setValue('subject', autoSubject, { shouldDirty: false });
  }, [data, typeCode, sequence, isEditMode, getValues, setValue, t]);

  const source = state?.source;
  const contentHtml = useMemo(
    () => (source ? buildSerpBulkStockAdjustmentContentHtml(source, t) : null),
    [source, t],
  );

  const renderBody = () => {
    if (!serpDocument) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('SERP 문서 정보가 없습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (contentHtml) {
      return <SerpBulkStockAdjustmentSnapshot content={contentHtml} />;
    }
    if (error) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  return (
    <FieldBlock className="serp-bulk-stock-adjustment">
      {renderBody()}
    </FieldBlock>
  );
};

export default SerpBulkStockAdjustmentField;
